import * as React from 'react';
import type { IOption, SelectOptionValue } from '../types';

interface IUseSelectAccessibilityIdsProps<TValue extends SelectOptionValue, TMetadata> {
	getOptionKey?: (option: IOption<TValue, TMetadata>) => string;
	id?: string;
}

interface IUseSelectAccessibilityIdsResult<TValue extends SelectOptionValue, TMetadata> {
	errorId?: string;
	getGroupSelectAllId: (groupValue: string) => string | undefined;
	getOptionId: (option: IOption<TValue, TMetadata>) => string | undefined;
	listboxId?: string;
	selectId?: string;
	triggerId?: string;
}

const RANDOM_ID_RADIX = 36;
const RANDOM_ID_SLICE_START = 2;
const useBrowserLayoutEffect: typeof React.useEffect = (effect, deps) => {
	const useEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;
	useEffect(effect, deps);
};

const createSelectId = (): string => {
	const randomId = globalThis.crypto?.randomUUID?.()
		?? `${Date.now().toString(RANDOM_ID_RADIX)}-${Math.random().toString(RANDOM_ID_RADIX).slice(RANDOM_ID_SLICE_START)}`;
	return `select-${randomId}`;
};

/**
 * Creates stable IDs linking a select trigger, listbox, options, and error.
 */
export const useSelectAccessibilityIds = <TValue extends SelectOptionValue, TMetadata>({
	getOptionKey,
	id,
}: IUseSelectAccessibilityIdsProps<TValue, TMetadata>): IUseSelectAccessibilityIdsResult<TValue, TMetadata> => {
	const [generatedId, setGeneratedId] = React.useState<string>();
	const selectId = id ?? generatedId;
	const triggerId = selectId ? `${selectId}-trigger` : undefined;
	const listboxId = selectId ? `${selectId}-listbox` : undefined;
	const errorId = selectId ? `${selectId}-error` : undefined;

	useBrowserLayoutEffect(() => {
		if (!id) {
			setGeneratedId(currentGeneratedId => currentGeneratedId ?? createSelectId());
		}
	}, [id]);

	const getOptionId = React.useCallback(
		(option: IOption<TValue, TMetadata>) => {
			if (!listboxId) {
				return undefined;
			}
			const optionKey = getOptionKey?.(option) ?? String(option.value);
			return `${listboxId}-option-${encodeURIComponent(optionKey)}`;
		},
		[getOptionKey, listboxId]
	);

	const getGroupSelectAllId = React.useCallback(
		(groupValue: string) =>
			listboxId
				? `${listboxId}-group-${encodeURIComponent(groupValue)}-select-all`
				: undefined,
		[listboxId]
	);

	return { errorId, getGroupSelectAllId, getOptionId, listboxId, selectId, triggerId };
};
